import { couldNot } from "@/helpers/userMessages";

// LSP-21931 — opening a Spark report that the caller can no longer read.
//
// The two read paths fail differently: a project report goes through contact-api, which rejects with
// { httpStatus: 403 | 404 }; a session report reads Scribe directly, whose getAnalysis RESOLVES null on
// a 404 and rejects an ApiError carrying { status } otherwise. Both ReportsPage and EntityReports used
// to open an empty dialog on the null and tell the user to "try again" on a 403/404 (or, on
// EntityReports, say nothing at all). Neither is something a retry fixes.
//
// contact-api answers 404 rather than 403 so it never confirms a report the caller cannot see, so the
// copy covers both "deleted" and "no access" without claiming which one it was.

export const REPORT_UNAVAILABLE_MESSAGE = "This report isn't available. It may have been deleted, or you may no longer have access to it.";

export interface ReportViewTarget {
	reportId: string;
	entityType: string;
	entityId: string;
}

interface ReportViewToast {
	info: (message: string) => void;
	error: (message: string) => void;
}

function httpStatusOf(error: unknown): unknown {
	if (!error || typeof error !== "object") return undefined;
	const { httpStatus, status } = error as { httpStatus?: unknown; status?: unknown };
	return httpStatus ?? status;
}

export function isReportUnavailableError(error: unknown): boolean {
	const httpStatus = httpStatusOf(error);
	return httpStatus === 403 || httpStatus === 404;
}

/** The read came back with no report — Scribe's getAnalysis resolves null on a 404. */
export function notifyReportUnavailable(toast: ReportViewToast, target: ReportViewTarget, reason: string): void {
	console.warn(`[ReportView] Report unavailable: ${reason}`, target);
	toast.info(REPORT_UNAVAILABLE_MESSAGE);
}

/** The read rejected. A 403/404 is "unavailable"; anything else is a failure on our side. */
export function notifyReportOpenFailed(toast: ReportViewToast, target: ReportViewTarget, error: unknown): void {
	if (isReportUnavailableError(error)) {
		notifyReportUnavailable(toast, target, `HTTP ${httpStatusOf(error)}`);
		return;
	}
	console.error("[ReportView] Opening report failed", { ...target, error });
	toast.error(couldNot("open the report"));
}
